import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiBarChart2, FiCamera, FiCheckSquare, FiClipboard, FiGrid } from 'react-icons/fi';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNavRefresh } from '../../context/NavContext';
import styles from './AdminLayout.module.css';

const NAV_GROUPS = [
  {
    label: 'Overview',
    links: [{ to: '/admin', label: 'Dashboard', icon: FiGrid, exact: true }],
  },
  {
    label: 'Assessment',
    links: [
      { to: '/admin/candidates', label: 'Candidates', icon: FiClipboard },
      { to: '/admin/questions', label: 'Questions', icon: FiCheckSquare },
      { to: '/admin/grading-system', label: 'Grading System', icon: FiBarChart2 },
    ],
  },
  {
    label: 'Results',
    links: [{ to: '/admin/results', label: 'Results', icon: FiBarChart2 }],
  },
  {
    label: 'Proctoring',
    links: [
      { to: '/admin/proctoring-violations', label: 'Violations', icon: FiAlertTriangle },
      { to: '/admin/super-admin/camera', label: 'Camera Control', icon: FiCamera },
    ],
  },
];

export default function AdminLayout({ children }) {
  const { admin, adminLogout } = useAuth();
  const { triggerRefresh, isLoading, setLoadingComplete } = useNavRefresh();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Clear loading when location (content) changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingComplete();
    }, 300); // Small delay to show loading state briefly

    return () => clearTimeout(timer);
  }, [location, setLoadingComplete]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    adminLogout();
    navigate('/admin/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleNavClick = () => {
    setMenuOpen(false);
    triggerRefresh();
  };

  return (
    <div className={styles.shell}>
      <aside className={`${styles.sidebar} ${menuOpen ? styles.open : ''}`}>
        <div className={styles.sidebarTop}>
          <div className={styles.brand}>
            <img src="/logo.png" alt="UAC Foods Logo" className={styles.brandMark} />
            <div className={styles.brandText}>
              <span className={styles.brandName}>UAC Foods</span>
              <span className={styles.brandSub}>Assessment Portal</span>
            </div>
          </div>
          <nav className={styles.nav}>
            {NAV_GROUPS.map(({ label: groupLabel, links }) => (
              <div className={styles.navGroup} key={groupLabel}>
                <span className={styles.navGroupLabel}>{groupLabel}</span>
                {links.map(({ to, label, icon: Icon, exact }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={exact}
                    className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
                    onClick={handleNavClick}
                  >
                    <span className={styles.navIcon}><Icon aria-hidden="true" /></span>
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </div>
        <div className={styles.sidebarBottom}>
          <div className={styles.adminInfo}>
            <div className={styles.adminAvatar}>{admin?.fullName?.[0]?.toUpperCase()}</div>
            <div className={styles.adminMeta}>
              <span className={styles.adminName}>{admin?.fullName}</span>
              <span className={styles.adminRole}>Administrator</span>
            </div>
          </div>
          <button className={styles.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.topbar}>
          <button className={styles.menuBtn} onClick={() => setMenuOpen(p => !p)}>
            <span /><span /><span />
          </button>
          <div className={styles.topbarRight}>
            <span className={styles.topbarUser}>{admin?.fullName}</span>
          </div>
        </header>
        <div className={styles.content}>{children}</div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
        </div>
      )}

      {menuOpen && <div className={styles.overlay} onClick={() => setMenuOpen(false)} />}

      {showLogoutModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Sign Out</h3>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to sign out? You will need to log in again to access the admin portal.</p>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalBtnCancel} onClick={cancelLogout}>Cancel</button>
              <button className={styles.modalBtnConfirm} onClick={confirmLogout}>Sign Out</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
